import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { ProjectStatus } from '../projects/types';
import type { PropItemStatus } from '../propItems/types';

export type PortalMilestoneId = 'prijato' | 'navrh' | 'vyroba' | 'pripraveno' | 'predano';
export interface PortalMilestone {
  current: PortalMilestoneId | 'cancelled';
  steps: Array<{ id: PortalMilestoneId; reached: boolean; at: string | null }>;
}

export interface PortalData {
  studioName: string;
  project: {
    code: string;
    name: string;
    subtitle: string | null;
    status: ProjectStatus;
    deadline: string | null;
  };
  propItems: Array<{
    id: string;
    name: string;
    description: string | null;
    status: PropItemStatus;
    // Milníky z pohledu klienta (interní fáze se na portál neposílají).
    milestone: PortalMilestone;
  }>;
  quotes: Array<{
    id: string;
    version: number;
    title: string | null;
    department: string | null;
    status: 'sent' | 'accepted' | 'rejected';
    issuedAt: string;
    acceptedAt: string | null;
    currency: string;
    total: number;
    billing: PortalBilling | null;
  }>;
  billingSummary: { currency: string; ordered: number; invoiced: number; paid: number } | null;
  photos: Array<{ id: string; caption: string | null }>;
  generatedAt: string;
}

// Stav fakturace, jak ho vidí klient (backend: toPublicBilling).
export interface PortalBilling {
  number: string;
  issuedAt: string;
  dueAt: string | null;
  sent: boolean;
  sentAt: string | null;
  paid: boolean;
  paidAt: string | null;
  overdue: boolean;
  currency: string;
  total: number;
}

export const portalQuotePdfUrl = (token: string, quoteId: string) =>
  `/api/portal/${encodeURIComponent(token)}/quotes/${quoteId}/pdf`;

export const portalThumbUrl = (token: string, photoId: string) =>
  `/api/portal/${token}/photo/${photoId}/thumbnail`;
export const portalFileUrl = (token: string, photoId: string) =>
  `/api/portal/${token}/photo/${photoId}/file`;

export function usePortal(token: string) {
  return useQuery({
    queryKey: ['portal', token],
    queryFn: () => api.get<PortalData>(`/api/portal/${token}`),
    retry: false,
  });
}

// --- Správa sdílení (v detailu zakázky) ---
interface ShareResult {
  shareToken: string | null;
  shareExpiresAt: string | null;
}

export function useEnableShare(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts: { expiresInDays?: number; regenerate?: boolean }) =>
      api.post<ShareResult>(`/api/projects/${projectId}/share`, opts),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectId] }),
  });
}

export function useRevokeShare(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del(`/api/projects/${projectId}/share`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectId] }),
  });
}

// === Veřejná výloha / online návod výroby (QR) ===
export interface PublicPropData {
  studioName: string;
  studioWeb: string | null;
  prop: {
    name: string;
    description: string | null;
    story: string | null;
    status: PropItemStatus;
    createdAt: string;
  };
  projectName: string | null;
  production: string | null;
  photos: Array<{ id: string; caption: string | null }>;
  generatedAt: string;
}

export const propPublicThumbUrl = (token: string, photoId: string) =>
  `/api/portal/prop/${token}/photo/${photoId}/thumbnail`;
export const propPublicFileUrl = (token: string, photoId: string) =>
  `/api/portal/prop/${token}/photo/${photoId}/file`;

export function usePublicProp(token: string) {
  return useQuery({
    queryKey: ['portal-prop', token],
    queryFn: () => api.get<PublicPropData>(`/api/portal/prop/${token}`),
    retry: false,
  });
}

interface PropShareResult {
  publicToken: string | null;
}

export function useEnablePropShare(propItemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts: { regenerate?: boolean } = {}) =>
      api.post<PropShareResult>(`/api/prop-items/${propItemId}/public`, opts),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prop-items', propItemId] }),
  });
}

export function useRevokePropShare(propItemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del(`/api/prop-items/${propItemId}/public`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prop-items', propItemId] }),
  });
}
